<template>
  <admin-navbar>
    <div class="bg-white rounded shadow">
      <h6 class="text-base border-t-4 border-t-blue-500 rounded-t border-b p-3">
        Уулзалтын байршил, огноо
      </h6>

      <div v-if="isMode === 'A'" class="p-3 min-h-[80vh]">
        <div class="flex gap-5">
          <button
            @click="switchMode('B')"
            class="bg-blue-500 p-2 text-white text-xs rounded w-fit hover:bg-blue-600 mb-4"
          >
            <font-awesome-icon icon="fa-solid fa-plus" class="mr-1" />
            Өдөр, цаг нэмэх
          </button>
          <button
            @click="setDeleteTime()"
            class="bg-red-400 p-2 text-white text-xs rounded w-fit hover:bg-red-500 mb-4"
          >
            <font-awesome-icon icon="fa-solid fa-trash" class="mr-1" />
            {{ !isDeleteTime ? "Цаг устгах" : "Болих" }}
          </button>
        </div>

        <h3 class="font-semibold mb-2">Уулзалтын өдөр цаг</h3>
        <section class="grid grid-cols-4 gap-5">
          <div
            v-for="(times, date) in groupedMeetings"
            :key="date"
            class="space-y-2"
          >
            <div
              class="text-left w-full font-medium bg-gray-100 px-4 py-2 rounded transition"
            >
              {{ date }}
            </div>

            <div class="pl-6 space-y-1">
              <div
                v-for="time in times"
                :key="time.id"
                class="flex justify-center gap-2"
              >
                <button
                  v-if="isDeleteTime"
                  @click="deleteTime(time.id, date, time.meeting_time)"
                >
                  <font-awesome-icon
                    icon="fa-solid fa-trash"
                    class="bg-red-500 text-white p-1 rounded"
                  />
                </button>

                <div
                  class="text-sm text-gray-700 border-l-2 border-blue-500 pl-2 w-full"
                >
                  {{ time.meeting_time }}
                </div>
              </div>
            </div>
          </div>
        </section>

        <button
          @click="switchMode('C')"
          class="bg-blue-500 p-2 text-white text-xs rounded w-fit hover:bg-blue-600 mt-8 mb-4"
        >
          <font-awesome-icon icon="fa-solid fa-plus" class="mr-1" />
          Уулзалтын өрөө нэмэх
        </button>

        <h3 class="font-semibold mb-2">Уулзалтын өрөөнүүд</h3>
        <section
          class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mt-2"
        >
          <div
            v-for="room in meetingRooms"
            :key="room.id"
            class="border rounded p-3 shadow"
          >
            <img
              :src="room.room_pic"
              alt="room"
              class="w-full h-32 object-cover rounded mb-2"
            />
            <div>Өрөөний нэр: {{ room.room_number }}</div>
            <div>Багтаамж: {{ room.capacity }}</div>
            <button
              @click="updateRoom(room)"
              class="bg-blue-500 rounded text-xs text-white p-2 mt-2"
            >
              <font-awesome-icon icon="fa-solid fa-pen" class="mr-1" />
              Засах
            </button>
          </div>
        </section>
      </div>

      <YellowAddMeetingTime
        v-else-if="isMode === 'B'"
        @closeMode="switchMode('A')"
      />
      <YellowAddMeetingRoom
        v-else-if="isMode === 'C'"
        :data="selectedRoom"
        @closeMode="closeModeC"
      />
    </div>
  </admin-navbar>

  <SuccessModal v-if="success" :success="success" />
  <ErrorModal v-if="error" :error="error" />
</template>

<script>
import AdminNavbar from "@/components/admin/AdminNavbar.vue";
import adminInstance from "@/lib/adminInstance";
import { onMounted, ref, computed } from "vue";
import YellowAddMeetingRoom from "@/containers/YellowPage/admin/YellowAddMeetingRoom.vue";
import YellowAddMeetingTime from "@/containers/YellowPage/admin/YellowAddMeetingTime.vue";
import SuccessModal from "@/components/ui/SuccessModal.vue";
import ErrorModal from "@/components/ui/ErrorModal.vue";

export default {
  name: "AdminYellowMeetingInfo",
  components: {
    AdminNavbar,
    YellowAddMeetingTime,
    YellowAddMeetingRoom,
    SuccessModal,
    ErrorModal,
  },
  setup() {
    const isMode = ref("A");
    const meetingTimes = ref([]);
    const meetingRooms = ref([]);
    const isLoading = ref(false);
    const success = ref(null);
    const error = ref(null);

    const switchMode = async (item) => {
      isMode.value = item;
      if (isMode.value === "A") {
        await fetchMeetingTimes();
      }
    };

    const fetchMeetingTimes = async () => {
      try {
        isLoading.value = true;
        const res = await adminInstance.get(
          `/yellow-page/get/meeting-times-rooms`
        );
        if (res.status === 200) {
          meetingTimes.value = res.data.meetings.times;
          meetingRooms.value = res.data.meetings.rooms;
        }
      } catch (err) {
        console.error(err);
      } finally {
        isLoading.value = false;
      }
    };

    const groupedMeetings = computed(() => {
      const groups = {};
      for (const item of meetingTimes.value) {
        const date = item.meeting_date.split("T")[0];
        if (!groups[date]) groups[date] = [];
        groups[date].push(item);
      }
      return groups;
    });

    onMounted(fetchMeetingTimes);

    const selectedRoom = ref(null);
    const updateRoom = (room) => {
      selectedRoom.value = room;
      isMode.value = "C";
    };

    const isDeleteTime = ref(false);
    const setDeleteTime = () => {
      isDeleteTime.value = !isDeleteTime.value;
    };

    const deleteTime = async (id, date, time) => {
      if (!window.confirm(`${date} ${time} цагийг устгах уу?`)) {
        return;
      }

      try {
        const res = await adminInstance.delete(
          `/yellow-page/delete/meeting-time?id=${id}`
        );
        if (res.status === 200) {
          success.value = "Амжилттай устгалаа.";

          const index = meetingTimes.value.findIndex((mt) => mt.id === id);
          if (index !== -1) {
            meetingTimes.value.splice(index, 1);
          }

          setTimeout(() => {
            success.value = false;
          }, 5000);
        }
      } catch (err) {
        error.value = "Цаг устгахад алдаа гарлаа.";
        setTimeout(() => {
          error.value = false;
        }, 5000);
      }
    };

    const closeModeC = async () => {
      selectedRoom.value = null;
      await fetchMeetingTimes();
      isMode.value = "A";
    };

    return {
      meetingTimes,
      meetingRooms,
      groupedMeetings,
      isLoading,
      isMode,
      switchMode,
      selectedRoom,
      updateRoom,
      isDeleteTime,
      setDeleteTime,
      deleteTime,
      success,
      error,
      fetchMeetingTimes,
      closeModeC,
    };
  },
};
</script>
